// app.vue's sitewide @graph adds a BreadcrumbList to every page (and a FAQPage on
// /pricing), and many pages emit their own. Google flags duplicate structured data,
// so a page-level BreadcrumbList or FAQPage replaces the sitewide one: the page's
// version has the real page names. scripts/validate-unique-schema.mjs enforces it.
//
// The sitewide graph also carries an Article-family node on content pages
// (data/article-schema.ts). Its headline is the page's own <title>, so it reads in the
// page's language once the localized editions translate the head; a page that emits its
// own Article, BlogPosting, NewsArticle or TechArticle keeps that one instead.
import { isArticleType } from "../data/article-schema.ts";

export const SITEWIDE_SCHEMA_KEY = "workspace369-structured-data";
const UNIQUE_TYPES = ["BreadcrumbList", "FAQPage"];
const ARTICLE = "Article";

type HeadTag = {
  tag: string;
  key?: string;
  props?: Record<string, any>;
  innerHTML?: string;
  textContent?: string;
};

const hasType = (node: any, type: string) =>
  type === ARTICLE
    ? isArticleType(node?.["@type"])
    : Array.isArray(node?.["@type"]) ? node["@type"].includes(type) : node?.["@type"] === type;

const findTypes = (value: any, found: Set<string>) => {
  if (!value || typeof value !== "object") return found;
  for (const type of [...UNIQUE_TYPES, ARTICLE]) if (hasType(value, type)) found.add(type);
  for (const child of Array.isArray(value) ? value : Object.values(value)) findTypes(child, found);
  return found;
};

const parseJson = (source: unknown) => {
  if (typeof source !== "string") return null;
  try {
    return JSON.parse(source);
  } catch {
    return null;
  }
};

export const dropSitewideDuplicates = (tags: HeadTag[]) => {
  const scripts = tags.filter(
    (tag) => tag.tag === "script" && tag.props?.type === "application/ld+json",
  );
  const sitewide = scripts.find((tag) => tag.key === SITEWIDE_SCHEMA_KEY);
  if (!sitewide) return;

  const schema = parseJson(sitewide.innerHTML);
  if (!Array.isArray(schema?.["@graph"])) return;

  const pageTypes = new Set<string>();
  for (const tag of scripts) {
    if (tag !== sitewide) findTypes(parseJson(tag.innerHTML), pageTypes);
  }

  schema["@graph"] = schema["@graph"].filter(
    (node: any) => ![...pageTypes].some((type) => hasType(node, type)),
  );

  const title = tags.find((tag) => tag.tag === "title");
  const headline = (title?.textContent ?? title?.innerHTML ?? "").trim();
  const article = schema["@graph"].find((node: any) => hasType(node, ARTICLE));
  if (article && headline) article.headline = headline;

  sitewide.innerHTML = JSON.stringify(schema);
};
